import React, { useState, useCallback, useEffect } from "react";
import { Card, Button, Table, Space, Modal, message, Badge, Tag, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { useLoadList, useDelete, useTableSelection } from "../../../hooks";
import { masterDataService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import { formatExchangeRatePeriod } from "./lib/exchangeRatePeriod";
import ExchangeRateFilter from "./components/ExchangeRateFilter";
import ExchangeRateForm from "./components/ExchangeRateForm";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";

const { confirm } = Modal;

const ExchangeRateList = () => {
    // 获取权限相关功能
    const { hasPermission } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [formVisible, setFormVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [currencies, setCurrencies] = useState([]);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("exchangeRates", masterDataService.exchangeRate.getExchangeRateList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, clearSelection } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    // 删除汇率的Hook
    const deleteMutation = useDelete("汇率", masterDataService.exchangeRate.deleteExchangeRate, {
        onSuccess: () => {
            refetch();
            clearSelection();
        },
    });

    // 加载货币列表
    const loadCurrencies = useCallback(async () => {
        try {
            const response = await masterDataService.exchangeRate.getCurrencies();
            setCurrencies(response.data?.currencies || []);
        } catch (error) {
            console.error("加载货币列表失败:", error);
        }
    }, []);

    // 组件初始化时加载货币列表
    useEffect(() => {
        loadCurrencies();
    }, [loadCurrencies]);

    // 处理搜索
    const handleSearch = useCallback((params) => {
        setFilterParams(params);
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    // 处理重置
    const handleReset = useCallback(() => {
        setFilterParams({});
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    // 处理刷新
    const handleRefresh = useCallback(() => {
        refetch();
    }, [refetch]);

    // 处理表格变化（分页、排序等）
    const handleTableChange = useCallback((paginationInfo) => {
        setPagination({
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        });
    }, []);

    // 处理新增
    const handleCreate = () => {
        setCurrentRecord(null);
        setFormVisible(true);
    };

    // 处理编辑
    const handleEdit = (record) => {
        setCurrentRecord(record);
        setFormVisible(true);
    };

    // 处理表单成功
    const handleFormSuccess = () => {
        setFormVisible(false);
        setCurrentRecord(null);
        refetch();
    };

    // 处理表单取消
    const handleFormCancel = () => {
        setFormVisible(false);
        setCurrentRecord(null);
    };

    // 处理单个删除
    const handleDelete = (record) => {
        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除汇率 "${record.currency_pair}" 吗？`,
            okText: "确认",
            cancelText: "取消",
            onOk: () => {
                deleteMutation.mutate(record.id);
            },
        });
    };

    // 处理批量删除
    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要删除的记录");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 条汇率记录吗？`,
            okText: "确认",
            cancelText: "取消",
            onOk: async () => {
                try {
                    await masterDataService.exchangeRate.batchDeleteExchangeRates({
                        ids: selectedRowKeys
                    });
                    message.success(`成功删除 ${selectedRowKeys.length} 条记录`);
                    refetch();
                    clearSelection();
                } catch (error) {
                    message.error("批量删除失败: " + (error.message || "未知错误"));
                }
            },
        });
    };

    // 渲染汇率状态
    const renderStatus = (isCurrentEffective, effectiveTo) => {
        if (isCurrentEffective) {
            return <Badge status="success" text="有效" />;
        } else {
            return <Badge status="error" text="已过期" />;
        }
    };

    // 渲染汇率来源
    const renderSource = (source, sourceDisplay) => {
        const colorMap = {
            "CENTRAL_BANK": "blue",
            "COMMERCIAL_BANK": "green", 
            "API": "orange",
            "MANUAL": "default",
        };

        return (
            <Tag color={colorMap[source] || "default"}>
                {sourceDisplay || source || "未知"}
            </Tag>
        );
    };

    // 渲染汇率值
    const renderRate = (rate) => {
        return (
            <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
                {Number(rate).toFixed(6)}
            </span>
        );
    };

    // 渲染记录状态（启用/禁用）
    const renderRecordStatus = (recordStatus) => {
        if (recordStatus === 'ACTIVE') {
            return <Badge status="success" text="启用" />;
        } else {
            return <Badge status="default" text="禁用" />;
        }
    };

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "货币对",
            dataIndex: "currency_pair",
            key: "currency_pair",
            width: 120,
            align: "center",
            render: (text, record) => (
                <span style={{ fontWeight: 500 }}>
                    {text}
                </span>
            ),
        },
        {
            title: "汇率",
            dataIndex: "rate",
            key: "rate",
            width: 120,
            align: "center",
            render: renderRate,
        },
        {
            title: "有效期间",
            dataIndex: "effective_from",
            key: "effective_period",
            width: 220,
            align: "center",
            render: (_, record) => {
                const periodText = formatExchangeRatePeriod(record.effective_from, record.effective_to);
                return (
                    <Tooltip title={periodText}>
                        <span style={{ fontSize: "12px" }}>
                            {periodText}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "汇率来源",
            dataIndex: "source",
            key: "source",
            width: 120,
            align: "center",
            render: (source, record) => renderSource(source, record.source_display),
        },
        {
            title: "时效状态",
            dataIndex: "is_current",
            key: "is_current",
            width: 100,
            align: "center",
            render: (isCurrentEffective, record) => renderStatus(isCurrentEffective, record.effective_to),
        },
        {
            title: "记录状态",
            dataIndex: "record_status",
            key: "record_status",
            width: 100,
            align: "center",
            render: renderRecordStatus,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            align: "center",
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 120,
            align: "center",
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="masterdata:update">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        >
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:delete">
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                        >
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title">
                    <h2>汇率管理</h2>
                    <span className="page-description">管理系统汇率数据，支持历史汇率查询</span>
                </div>
                <div className="page-actions">
                    <Space>
                        <Button
                            icon={<SyncOutlined />}
                            onClick={handleRefresh}
                            loading={isLoading}
                        >
                            刷新
                        </Button>
                        <PermissionGuard permission="masterdata:delete">
                            <Button
                                danger
                                disabled={selectedRowKeys.length === 0}
                                onClick={handleBatchDelete}
                            >
                                批量删除 ({selectedRowKeys.length})
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="masterdata:create">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                            >
                                新增汇率
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            {/* 筛选条件 */}
            <Card className="filter-card" size="small">
                <ExchangeRateFilter
                    onSearch={handleSearch}
                    onReset={handleReset}
                    onRefresh={handleRefresh}
                    currencies={currencies}
                />
            </Card>

            {/* 数据表格 */}
            <Card className="data-card">
                <Table
                    ref={tableRef}
                    columns={columns}
                    dataSource={data?.items || []}
                    loading={isLoading}
                    rowKey="id"
                    rowSelection={hasPermission("masterdata:delete") ? rowSelection : null}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* 新增/编辑表单 */}
            <ExchangeRateForm
                visible={formVisible}
                onCancel={handleFormCancel}
                onSuccess={handleFormSuccess}
                editingRecord={currentRecord}
                currencies={currencies}
            />
        </div>
    );
};

export default ExchangeRateList;
